<script>  
import { onMounted, onUnmounted, watch } from 'vue'  
import { collection, addDoc, onSnapshot, query, orderBy, where, updateDoc, doc } from 'firebase/firestore'  
import { db } from '@/main'  
import { globalState } from '@/globalState'  
  
export default {  
  name: 'VoiceChat',  
  props: {  
    isActive: { type: Boolean, default: false },  
    userId: { type: String, required: true }  
  },  
  emits: ['update:isActive'],  
  setup(props, { emit }) {  
    // --- Internal state ---  
    const internalActive = { value: false }  
    let recognizer = null  
    let synthesizer = null  
    let silenceTimer = null  
    let repliesUnsubscribe = null  
    let currentAudio = null  
    let currentAudioUrl = null  
    let processedReplies = new Set()  
    // Guards  
    let isTotallyUnmounted = false  
    let cancelSpeak = false  
  
    // Azure Speech Service credentials  
    const SPEECH_KEY = '0b8102b3da354994822bcbe1e1e2d9e4'  
    const SPEECH_REGION = 'eastus'  
  
    // --- Watch userId and (re)setup Firestore replies listeners ---  
    function cleanupRepliesListener() {  
      if (repliesUnsubscribe) {  
        repliesUnsubscribe()  
        repliesUnsubscribe = null  
      }  
    }  
  
    function setupRepliesListener(userId) {  
      cleanupRepliesListener()  
      if (!userId) return // Don't start listener if no user  
      const repliesQuery = query(  
        collection(db, 'replies', userId, 'replies'),  
        where('isDone', '==', false),  
        orderBy('timestamp', 'desc')  
      );  
      repliesUnsubscribe = onSnapshot(repliesQuery, (snapshot) => {  
        const now = new Date()  
        snapshot.docChanges().forEach((change) => {  
          if (  
            isTotallyUnmounted || cancelSpeak ||  
            !internalActive.value  
          ) return  
          if (change.type === 'added') {  
            const docId = change.doc.id  
            const reply = change.doc.data()  
            const replyTime = reply.timestamp?.toDate ? reply.timestamp.toDate() : new Date(0)  
            const timeDiff = (now - replyTime) / 1000  
            if (timeDiff > 30) {  
              updateDoc(doc(db, 'replies', userId, 'replies', docId), { isDone: true })  
              return  
            }  
            if (!internalActive.value || isTotallyUnmounted || cancelSpeak) {  
              updateDoc(doc(db, 'replies', userId, 'replies', docId), { isDone: true })  
              return  
            }  
            if (processedReplies.has(docId)) return  
            processedReplies.add(docId)  
            speakReply(reply.text, docId)  
          }  
        })  
      })  
    }  
  
    watch(  
      () => props.userId,  
      (uid, oldUid) => {  
        processedReplies.clear()  
        setupRepliesListener(uid)  
      },  
      { immediate: true }  
    )  
  
    // --- On mount (Speech SDK only, no listener) ---  
    onMounted(() => {  
      isTotallyUnmounted = false  
      cancelSpeak = false  
      loadSpeechSDK()  
    })  
  
    // --- On unmount ---  
    onUnmounted(() => {  
      isTotallyUnmounted = true  
      cancelSpeak = true  
      stopVoiceChat()  
      cleanupAudio()  
      cleanupRepliesListener()  
      // Reset global state for UI  
      globalState.isVoiceListening = false  
      globalState.isVoiceSpeaking = false  
      globalState.voiceTranscript = ''  
      globalState.voiceStatus = 'Ready'  
    })  
  
    // --- Watch prop from parent to toggle  
    watch(() => props.isActive, (newValue) => {  
      globalState.isVoiceActive = newValue  
      if (newValue && !internalActive.value) {  
        processedReplies.clear()  
        internalActive.value = true  
        isTotallyUnmounted = false  
        cancelSpeak = false  
        startVoiceChat()  
      } else if (!newValue) {  
        internalActive.value = false  
        cancelSpeak = true  
        stopListening()  
        cleanupAudio()  
        if (globalState.isVoiceSpeaking) stopSpeaking()  
        globalState.voiceStatus = 'Ready'  
        globalState.isVoiceListening = false  
        globalState.isVoiceSpeaking = false  
      }  
    }, { immediate: true })  
  
    // --- Audio cleanup ---  
    function cleanupAudio() {  
      if (currentAudio) {  
        try { currentAudio.pause(); currentAudio.currentTime = 0 } catch (_) {}  
        currentAudio = null  
      }  
      if (currentAudioUrl) {  
        try { URL.revokeObjectURL(currentAudioUrl) } catch (_) {}  
        currentAudioUrl = null  
      }  
    }  
  
    // --- Speech SDK loader ---  
    function loadSpeechSDK() {  
      if (window.SpeechSDK) return Promise.resolve()  
      return new Promise((resolve) => {  
        const script = document.createElement('script')  
        script.src = 'https://aka.ms/csspeech/jsbrowserpackageraw'  
        script.onload = resolve  
        document.head.appendChild(script)  
      })  
    }  
  
    // --- Speech Services ---  
    async function initializeSpeechServices() {  
      if (isTotallyUnmounted) return  
      await loadSpeechSDK()  
      const speechConfig = window.SpeechSDK.SpeechConfig.fromSubscription(SPEECH_KEY, SPEECH_REGION)  
      speechConfig.speechRecognitionLanguage = 'en-US'  
      speechConfig.speechSynthesisLanguage = 'en-US'  
      speechConfig.speechSynthesisVoiceName = 'en-US-AshleyNeural'  
      const audioConfig = window.SpeechSDK.AudioConfig.fromDefaultMicrophoneInput()  
      recognizer = new window.SpeechSDK.SpeechRecognizer(speechConfig, audioConfig)  
      synthesizer = new window.SpeechSDK.SpeechSynthesizer(speechConfig)  
    }  
  
    // --- Lifecycle handlers ---  
    async function startVoiceChat() {  
      if (isTotallyUnmounted) return  
      try {  
        await initializeSpeechServices()  
        startListening()  
      } catch (error) {  
        globalState.voiceStatus = 'Error'  
      }  
    }  
  
    function stopVoiceChat() {  
      stopListening()  
      stopSpeaking()  
      cleanupAudio()  
      globalState.voiceStatus = 'Ready'  
    }  
  
    // --- Recognizer handlers ---  
    function startListening() {  
      if (isTotallyUnmounted || cancelSpeak) return  
      if (!recognizer) return  
      globalState.isVoiceListening = true  
      globalState.voiceStatus = 'Listening...'  
      globalState.voiceTranscript = ''  
      recognizer.recognizing = (s, e) => {  
        if (isTotallyUnmounted || cancelSpeak) return  
        globalState.voiceTranscript = e.result.text  
        restartSilenceTimer()  
      }  
      recognizer.recognized = (s, e) => {  
        if (isTotallyUnmounted || cancelSpeak) return  
        if (e.result.reason === window.SpeechSDK.ResultReason.RecognizedSpeech) {  
          globalState.voiceTranscript = e.result.text  
          restartSilenceTimer()  
        }  
      }  
      recognizer.startContinuousRecognitionAsync()  
    }  
  
    function stopListening() {  
      if (recognizer) {  
        try { recognizer.stopContinuousRecognitionAsync() } catch (_) {}  
      }  
      if (silenceTimer) clearTimeout(silenceTimer)  
      globalState.isVoiceListening = false  
    }  
  
    function restartSilenceTimer() {  
      if (silenceTimer) clearTimeout(silenceTimer)  
      silenceTimer = setTimeout(() => { handleEndOfTranscript() }, 2000)  
    }  
  
    async function handleEndOfTranscript() {  
      if (isTotallyUnmounted || cancelSpeak) return  
      if (silenceTimer) clearTimeout(silenceTimer)  
      if (!globalState.voiceTranscript.trim()) return  
      stopListening()  
      globalState.voiceStatus = 'Processing...'  
      try {  
        if (!props.userId) throw new Error('No userId!')  
        await addDoc(  
          collection(db, 'transcripts', props.userId, 'transcripts'),  
          {  
            text: globalState.voiceTranscript,  
            isRead: false,  
            isConvoMode: true,  
            modelUsed: 'gpt4o',  
            isAnalyzed: false,  
            createdAt: new Date()  
          }  
        )  
        globalState.voiceStatus = 'Waiting for response...'  
      } catch (error) {  
        globalState.voiceStatus = 'Error'  
        setTimeout(() => {  
          if (!isTotallyUnmounted && !cancelSpeak) {  
            startListening()  
            emit('update:isActive', true)  
          }  
        }, 1000)  
      }  
      globalState.voiceTranscript = ''  
    }  
  
    // --- Speech Synthesis ---  
    async function speakReply(text, docId) {  
      if (!synthesizer || !text) return  
      if (isTotallyUnmounted || cancelSpeak || !internalActive.value) {  
        cleanupAudio()  
        globalState.isVoiceSpeaking = false  
        return  
      }  
      cleanupAudio()  
      globalState.isVoiceSpeaking = true  
      globalState.voiceStatus = 'Speaking...'  
      const ssml = convertTextToStyledSSML(text, 'en-US-AshleyNeural', 'en-US', 'whispering', 1.0, 1.12)  
      try {  
        await new Promise((resolve, reject) => {  
          synthesizer.speakSsmlAsync(  
            ssml,  
            async (result) => {  
              if (isTotallyUnmounted || cancelSpeak || !internalActive.value) {  
                globalState.isVoiceSpeaking = false  
                cleanupAudio()  
                return resolve(result)  
              }  
              const audioBlob = new Blob([result.audioData], { type: 'audio/wav' })  
              currentAudioUrl = URL.createObjectURL(audioBlob)  
              currentAudio = new Audio(currentAudioUrl)  
  
              currentAudio.onended = async () => {  
                if (isTotallyUnmounted || cancelSpeak || !internalActive.value) {  
                  globalState.isVoiceSpeaking = false  
                  cleanupAudio()  
                  return resolve(result)  
                }  
                globalState.isVoiceSpeaking = false  
                cleanupAudio()  
                if (docId) {  
                  try {  
                    await updateDoc(doc(db, 'replies', props.userId, 'replies', docId), { isDone: true })  
                  } catch {}  
                }  
                if (internalActive.value && !isTotallyUnmounted && !cancelSpeak) {  
                  setTimeout(() => {  
                    if (!isTotallyUnmounted && !cancelSpeak) startListening()  
                  }, 500)  
                } else {  
                  globalState.voiceStatus = 'Ready'  
                }  
                resolve(result)  
              }  
              currentAudio.onerror = (error) => {  
                cleanupAudio()  
                globalState.isVoiceSpeaking = false  
                reject(error)  
              }  
              currentAudio.play().catch((playError) => {  
                cleanupAudio()  
                globalState.isVoiceSpeaking = false  
                reject(playError)  
              })  
            },  
            (error) => {  
              globalState.isVoiceSpeaking = false  
              reject(error)  
            }  
          )  
        })  
      } catch (error) {  
        globalState.isVoiceSpeaking = false  
        cleanupAudio()  
      }  
    }  
  
    function stopSpeaking() {  
      cleanupAudio()  
      if (synthesizer) {  
        try { synthesizer.close() } catch (_) {}  
        synthesizer = null  
      }  
      globalState.isVoiceSpeaking = false  
      setTimeout(async () => {  
        if (!isTotallyUnmounted && !cancelSpeak) {  
          await initializeSpeechServices()  
        }  
      }, 100)  
    }  
  
    function convertTextToStyledSSML(text, voice, lang, style, styleDegree, rate = 1.0) {  
      const escapedText = text  
        .replace(/&/g, '&amp;')  
        .replace(/</g, '&lt;')  
        .replace(/>/g, '&gt;')  
        .replace(/"/g, '&quot;')  
        .replace(/'/g, '&apos;')  
      return `  
      <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="https://www.w3.org/2001/mstts" xml:lang="${lang}">  
        <voice name="${voice}">  
          <prosody rate="${rate}">  
            <mstts:express-as style="${style}" styledegree="${styleDegree}">  
              ${escapedText}  
            </mstts:express-as>  
          </prosody>  
        </voice>  
      </speak>`.trim()  
    }  
  
    watch(  
      () => globalState.shouldStopListening,  
      (val) => {  
        if (val) {  
          stopListening()  
          globalState.shouldStopListening = false // reset flag  
        }  
      }  
    )  
  
    // No template needed  
    return {}  
  }  
}  
</script>  
